import { useEffect, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { Integration } from "../../../models/Entities";
import {
  navigateToBlueprintEditor,
  navigateToScraperEditor,
  navigateToScraperHistory,
} from "../../../router/RouterUtils";

import { ScraperDataType, Scraper } from "../../scraper/types";
import { deleteScraper, fetchScrapers } from "../../scraper/utils/ScraperAPIClient";
import DeletionConfirmationModal from "../../shared/DeletionConfirmationModal";
import EmptyStateWrapper from "../../shared/layout/EmptyStateWrapper";
import { showSuccessToast, showErrorToast } from "../../shared/Toasts";
import useDocumentTitle from "../../shared/useDocumentTitle";
import ScrapersTableCreateButton from "./ScrapersTableCreateButton";
import { Button, Badge, ButtonVariant, Card, Text } from "@merge-api/merge-javascript-shared";
import { List, Clipboard, Trash2, Edit2 } from "lucide-react";

type Props = { integrationMeta: Integration };

type RouteParams = {
  integrationID: string;
};

const ScrapersTable = ({ integrationMeta }: Props) => {
  const history = useHistory();
  const { integrationID } = useParams<RouteParams>();
  const [scrapers, setScrapers] = useState<Scraper[]>();
  const [isLoading, setIsLoading] = useState(false);
  const [isShowingConfirmDeleteScraperModal, setIsShowingConfirmDeleteScraperModal] = useState<
    boolean
  >(false);
  const [selectedScraperID, setSelectedScraperID] = useState<string | undefined>();
  const [selectedScraperName, setSelectedScraperName] = useState<string | undefined>();

  useDocumentTitle(`${integrationMeta.name} - Scrapers`, [integrationMeta.name]);

  useEffect(() => {
    fetchScrapers({ integrationID, onSuccess: setScrapers });
  }, [integrationID]);

  const viewEditor = (integrationID: string, blueprintID: string) => {
    navigateToBlueprintEditor(history, integrationID, blueprintID);
  };

  const viewScraperHistory = (
    integrationID: string,
    scraperID: string,
    navigateToNewTab: boolean
  ) => {
    navigateToScraperHistory(history, integrationID, scraperID, undefined, navigateToNewTab);
  };

  return (
    <>
      <DeletionConfirmationModal
        selectedObjectType="Scraper"
        show={isShowingConfirmDeleteScraperModal}
        onHide={() => setIsShowingConfirmDeleteScraperModal(false)}
        isLoading={isLoading}
        onConfirm={() => {
          setIsLoading(true);
          if (selectedScraperID) {
            deleteScraper({
              scraperID: selectedScraperID,
              onSuccess: () => {
                showSuccessToast(`Successfully deleted Scraper!`);
                setIsShowingConfirmDeleteScraperModal(false);
                fetchScrapers({ integrationID, onSuccess: setScrapers });
                setIsLoading(false);
              },
              onError: () => {
                showErrorToast(`Failed to delete Scraper.`);
                setIsLoading(false);
              },
            });
          }
        }}
        objectName={selectedScraperName}
      />

      <Card>
        <div className="grid grid-cols-3 px-4 py-3 border-b border-gray-10">
          <div className="col-span-1">
            <Text variant="h6">Name</Text>
          </div>
          <div className="col-span-1">
            <Text variant="h6">Data source</Text>
          </div>
          <div className="col-span-1 flex justify-end">
            <ScrapersTableCreateButton integrationID={integrationID} />
          </div>
        </div>
        {scrapers ? (
          scrapers.length > 0 ? (
            scrapers.map((scraper, index) => (
              <div
                key={scraper.id}
                className={`grid grid-cols-3 px-4 py-3 ${
                  index !== scrapers.length - 1 ? "border-b" : ""
                } border-gray-10 hover:bg-gray-0 transition-colors cursor-pointer`}
              >
                <div className="col-span-1 flex items-center">{scraper.name}</div>
                <div className="col-span-1 flex items-center">
                  <Badge color={scraper.data_type === ScraperDataType.AUTH_DATA ? "red" : "yellow"}>
                    {scraper.data_type === ScraperDataType.AUTH_DATA
                      ? "Auth scraper"
                      : "Common model scraper"}
                  </Badge>
                </div>
                <div className="col-span-1 flex flex-row gap-x-3 justify-end items-center">
                  <Button
                    variant={ButtonVariant.SecondaryGray}
                    size="sm"
                    onClick={(event) =>
                      viewScraperHistory(integrationID, scraper.id, event.metaKey || event.ctrlKey)
                    }
                    leftIcon={<List size={12} />}
                  >
                    Execution history
                  </Button>
                  <Button
                    variant={ButtonVariant.TertiaryWhite}
                    size="sm"
                    onClick={() =>
                      viewEditor(integrationID, scraper.most_recent_blueprint_version_id)
                    }
                    leftIcon={<Clipboard size={12} />}
                  >
                    View blueprint
                  </Button>
                  <Button
                    variant={ButtonVariant.TertiaryWhite}
                    size="sm"
                    onClick={() =>
                      navigateToScraperEditor(
                        history,
                        integrationID,
                        scraper.id,
                        scraper.most_recent_scraper_version_id
                      )
                    }
                    leftIcon={<Edit2 size={12} />}
                  >
                    Edit scraper
                  </Button>
                  <Button
                    variant={ButtonVariant.DangerFilled}
                    size="sm"
                    onClick={() => {
                      setIsShowingConfirmDeleteScraperModal(true);
                      setSelectedScraperID(scraper.id);
                      setSelectedScraperName(scraper.name);
                    }}
                    leftIcon={<Trash2 size={12} />}
                  >
                    Delete scraper
                  </Button>
                </div>
              </div>
            ))
          ) : (
            <div className="p-0 w-full">
              <EmptyStateWrapper title="No Scrapers" />
            </div>
          )
        ) : (
          <div className="p-0 w-full">
            <EmptyStateWrapper isSpinner />
          </div>
        )}
      </Card>
    </>
  );
};

export default ScrapersTable;
